import { type AnchorHTMLAttributes, type HTMLAttributes, memo, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { type IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faHexagonExclamation, faCircleInfo } from '@fortawesome/pro-solid-svg-icons';
import { Trans, useTranslation } from 'react-i18next';
import { Divider as HeroUIDivider } from '@heroui/react';
import { twMerge } from 'tailwind-merge';
import { type Nullable } from '@Types';
import { Button } from 'Button';
import { Checkbox } from 'Checkbox';
import { Radio, RadioGroup } from 'Radio';
import { Text } from 'Text';
import { FileInput, fileTypeValidator, maxSizeMBValidator } from 'FileInput';
import { Input } from 'Input';
import {
    type LearnerProject,
    type SubmissionFormatType,
    SUBMISSION_FORMAT_GITHUB_LINK,
    SUBMISSION_FORMAT_PRESENTATION_LINK,
    SUBMISSION_FORMAT_PDF,
    SUBMISSION_FORMAT_SPREADSHEET,
    SUBMISSION_FORMAT_ZIP,
    SUBMISSION_FORMAT_GENERIC_LINK,
} from 'LearnerProjects';
import { FormProvider, useForm, useFormContext, useWatch, yupResolver } from 'FrontRoyalReactHookForm';
import * as Yup from 'yup';
import './locales/submission_form-en.json';
import { useCurrentUser, useTargetBrandConfig } from 'FrontRoyalAngular';
import { type CurrentUser, getCohort } from 'Users';
import FrontRoyalSpinner from 'FrontRoyalSpinner';
import { MailtoLink } from 'MailtoLink';
import { useContentProgress } from 'ContentProgress';
import { RenderMarkdown } from 'MarkdownComponents';
import {
    type ProjectGroupMembershipSansProjectGroup,
    type ProjectGroupWithMemberships,
    getMemberDetailsFromProjectGroupMembership,
    MembersList,
    ProjectGroupMembershipStatus,
} from 'ProjectGroup';
import {
    type AdminUploadTargetUser,
    type ProjectSubmissionUploadAttrs,
    type ProjectSubmissionAttachment,
    type UploadProjectSubmissionResponse,
} from './ProjectSubmission.types';
import { MAX_FILE_SIZE_MB, MIME_TYPE_PDF, MIME_TYPE_XLSX, MIME_TYPE_ZIP } from './constants';
import { uploadProjectSubmissionApi } from './uploadProjectSubmissionApi';
import { isValidUrl, isValidGitHubUrl } from './urlValidation';

// Form values for a single submission format (file or URL, not both)
type SubmissionFormatFormValue = {
    files: File[];
    url: string;
};

// A server-side rejection tied to one submission format's link input. `url` is the value that was
// rejected, so the message can stop showing as soon as the student edits the field.
type ServerFieldError = { message: string; url: string };
type ServerFieldErrors = Partial<Record<SubmissionFormatType, ServerFieldError>>;

// Shared so that clearing errors keeps a stable identity and doesn't re-render every memoized
// format section on each submit.
const NO_SERVER_FIELD_ERRORS: ServerFieldErrors = {};

// See the comment in BackRoyalApi/convertCasing/snakeCaseQueryBody re. FormData for why these aren't camelcased.
const formatFormValues = (
    currentUser: CurrentUser | null,
    project: LearnerProject,
    values: ProjectSubmissionFormValues,
    adminContext?: { targetUser: AdminUploadTargetUser; cohortId: string },
): ProjectSubmissionUploadAttrs => {
    const attachments: ProjectSubmissionAttachment[] = [];

    const submissionsByFormat = values.submissionsByFormat as Record<SubmissionFormatType, SubmissionFormatFormValue>;
    (project.submissionFormats ?? []).forEach(format => {
        const formatValue = submissionsByFormat[format.type];
        if (!formatValue) return;

        if (formatValue.files && formatValue.files.length > 0) {
            attachments.push({
                submission_format_type: format.type,
                file: formatValue.files[0],
            });
        } else if (formatValue.url && formatValue.url.trim()) {
            attachments.push({
                submission_format_type: format.type,
                url: formatValue.url.trim(),
            });
        }
    });

    // In admin mode, use the target user's ID and provided cohort ID
    const userId = adminContext?.targetUser.id || currentUser?.id || '';
    const cohortId = adminContext?.cohortId || getCohort(currentUser)?.id || '';

    return {
        group_project_agreement_file: values.groupProjectAgreementFiles?.[0] || null,
        metadata: { cohort_id: cohortId, ...(adminContext ? { from_project_eval_modal: true } : {}) },
        project_type: project.projectType,
        requirement_identifier: project.requirementIdentifier,
        submitted_for_learner_project_id: project.id,
        user_id: userId,
        attachments,
        selected_project_variant: values.selectedProjectVariant || null,
    };
};

type SubmissionsByFormatValidationContext = {
    submissionFormats: Array<{ type: SubmissionFormatType }>;
    hasVariants: boolean;
};

const projectSubmissionFormSchema = Yup.object()
    .shape({
        selectedProjectVariant: Yup.string().when('$hasVariants', {
            is: true,
            then: schema => schema.required('Please select a project option'),
            otherwise: schema => schema.notRequired(),
        }),
        groupProjectAgreementFiles: Yup.array().when(['$isGroupProject', '$collaborationType'], {
            is: (isGroupProject: boolean, collaborationType: string) => isGroupProject && collaborationType === 'group',
            then: schema => schema.of(Yup.mixed()).max(1).min(1, 'Group project agreement is required'),
            otherwise: schema => schema.notRequired(),
        }),
        confirmsProjectMeetsRequirements: Yup.boolean().oneOf(
            [true],
            'Please confirm that the project meets all requirements',
        ),
        confirmsGroupProjectSharedScoreAndFeedback: Yup.boolean().when(['$isGroupProject', '$collaborationType'], {
            is: (isGroupProject: boolean, collaborationType: string) => isGroupProject && collaborationType === 'group',
            then: schema =>
                schema.oneOf([true], 'Please confirm that all group members will share the same score and feedback'),
            otherwise: schema => schema.notRequired(),
        }),
        submissionsByFormat: Yup.object().test(
            'all-formats-filled',
            'All submission formats must be completed',
            function testAllFormatsFilled(value) {
                const ctx = this.options.context as SubmissionsByFormatValidationContext;
                const formats = ctx.submissionFormats;

                if (!value) return false;

                const submissionsByFormat = value as Record<string, SubmissionFormatFormValue>;

                // Find the first format with a validation error
                const invalidFormat = formats.find(format => {
                    const formValue = submissionsByFormat[format.type];
                    if (!formValue) return true;

                    const hasFile = formValue.files && formValue.files.length > 0;
                    const url = formValue.url?.trim();

                    // For PDF, spreadsheet, and ZIP formats: must have file
                    if (
                        format.type === SUBMISSION_FORMAT_PDF ||
                        format.type === SUBMISSION_FORMAT_SPREADSHEET ||
                        format.type === SUBMISSION_FORMAT_ZIP
                    ) {
                        return !hasFile;
                    }

                    // For GitHub link: must have a valid GitHub URL
                    if (format.type === SUBMISSION_FORMAT_GITHUB_LINK) {
                        return !url || !isValidGitHubUrl(url);
                    }

                    // For other link types: must have a valid URL
                    return !url || !isValidUrl(url);
                });

                // If we found an invalid format, create specific error for GitHub links
                if (invalidFormat) {
                    const formValue = submissionsByFormat[invalidFormat.type];
                    const url = formValue?.url?.trim();
                    if (invalidFormat.type === SUBMISSION_FORMAT_GITHUB_LINK && url && !isValidGitHubUrl(url)) {
                        return this.createError({
                            path: `submissionsByFormat.${invalidFormat.type}.url`,
                            message: 'Please provide a valid GitHub URL (e.g., https://github.com/username/repository)',
                        });
                    }
                    return false;
                }

                return true;
            },
        ),
    })
    .required();

type ProjectSubmissionFormValues = Yup.InferType<typeof projectSubmissionFormSchema>;

const SubHeader = ({ className, project }: { className: string; project: LearnerProject }) => (
    <Text as="h2" className={twMerge('text-center', className)} color="slate-grey" weight="normal" uppercase>
        {project.title}
    </Text>
);

const Banner = ({
    className,
    onButtonPress,
}: {
    className: {
        base: string;
        icon: { className: string; faIcon: IconDefinition };
        message: { className: string; tKey: string };
        button: { className: string; tKey: string };
    };
    onButtonPress?: () => void;
}) => {
    const { t } = useTranslation('back_royal');
    return (
        <div className={twMerge('rounded-lg border-1 p-4 pr-5', className.base)}>
            <div className="flex flex-col items-center gap-3 xs:flex-row">
                <div className="flex items-center gap-3">
                    <div className={twMerge('rounded-full border-1 p-1.5 shadow-smallish', className.icon.className)}>
                        <FontAwesomeIcon icon={className.icon.faIcon} size="lg" />
                    </div>
                    <div className={twMerge('flex flex-1 flex-col gap-1', className.message.className)}>
                        <Trans
                            i18nKey={className.message.tKey}
                            t={t}
                            components={{
                                medium: <p className="text-xs font-medium leading-4" />,
                                regular: <p className="text-xs leading-4" />,
                            }}
                        />
                    </div>
                </div>
                <div className="ml-4">
                    <Button
                        className={twMerge('bg-opacity-30 font-normal', className.button.className)}
                        height={8}
                        onPress={onButtonPress}
                    >
                        {t(className.button.tKey)}
                    </Button>
                </div>
            </div>
        </div>
    );
};

const Description = ({
    project,
    projectGroupMembership,
    onCreateGroup,
    onViewGroup,
    isAdminMode,
}: {
    project: LearnerProject;
    projectGroupMembership: Nullable<ProjectGroupMembershipSansProjectGroup>;
    onCreateGroup: () => void;
    onViewGroup: () => void;
    isAdminMode: boolean;
}) => {
    const { t } = useTranslation('back_royal');
    const brandConfig = useTargetBrandConfig();
    const projectEmail = brandConfig.emailAddressForUsername('projects');

    const isInAcceptedGroup =
        project.groupProject && projectGroupMembership?.status === ProjectGroupMembershipStatus.ACCEPTED;

    const bannerConfigs = {
        individually: {
            className: {
                base: 'border-blue-300 bg-blue-50 text-blue-800',
                icon: {
                    className: 'bg-blue-50',
                    faIcon: faCircleInfo,
                },
                message: {
                    className: 'text-blue-800',
                    tKey: 'submission_form.individual_submission_banner_message',
                },
                button: {
                    className: 'bg-blue-300 text-blue-800',
                    tKey: 'submission_form.create_group',
                },
            },
            onButtonPress: onCreateGroup,
        },
        groupProject: {
            className: {
                base: 'border-yellow-500 bg-yellow-50 text-yellow-900',
                icon: {
                    className: 'bg-yellow-50',
                    faIcon: faHexagonExclamation,
                },
                message: {
                    className: 'text-yellow-900',
                    tKey: 'submission_form.group_project_submission_banner_message',
                },
                button: {
                    className: 'bg-yellow-500 text-yellow-900',
                    tKey: 'submission_form.view_group',
                },
            },
            onButtonPress: onViewGroup,
        },
    };
    const bannerConfig = bannerConfigs[isInAcceptedGroup ? 'groupProject' : 'individually'];

    return (
        <div className="my-0">
            <Text className="whitespace-pre-line text-base" color="slate-grey" weight="normal">
                {t('submission_form.description', {
                    email: <MailtoLink email={projectEmail} key="projects-mailto" />,
                })}
            </Text>
            {project.groupProject && !isAdminMode && (
                <div className="mt-7.5">
                    <Banner className={bannerConfig.className} onButtonPress={bannerConfig.onButtonPress} />
                </div>
            )}
        </div>
    );
};

// Each displayed section has a horizontally oriented divider that separates that section's content from the previous section's content.
// The divider is responsible for the vertical margin between the sections - 15px after the previous section, 15px before the containing section.
const Divider = () => <HeroUIDivider className="-mx-7.5 my-7.5 w-auto" />;

const GroupProjectSection = ({
    project,
    projectGroupMembership,
}: {
    project: LearnerProject;
    projectGroupMembership: Nullable<ProjectGroupMembershipSansProjectGroup>;
}) => {
    const { t } = useTranslation('back_royal');
    if (!project.groupProject) return null;
    if (projectGroupMembership?.status !== ProjectGroupMembershipStatus.ACCEPTED) return null;

    return (
        <>
            <Divider />
            <div className="my-0">
                <Text className="mb-3.5 text-base" color="black" weight="normal">
                    {t('submission_form.group_project_agreement_description')}
                </Text>
                <FileInput
                    accept="application/pdf"
                    buttonText={t('submission_form.group_project_agreement_file_input_button_text') as string}
                    name="groupProjectAgreementFiles"
                    note={t('submission_form.group_project_agreement_file_input_note') as string}
                    validators={[maxSizeMBValidator(10)]}
                />
            </div>
        </>
    );
};

const VariantSelectionSection = ({ project }: { project: LearnerProject }) => {
    const { t } = useTranslation('back_royal');
    const variantConfig = project.variantConfig;

    // Don't render unless at least 2 variants are configured (a focus choice requires >= 2 options)
    if (!variantConfig?.variants || variantConfig.variants.length < 2) return null;

    return (
        <>
            <Divider />
            <div className="my-0">
                <Text as="h3" color="black" className="mb-[10px]">
                    {t('submission_form.variant_selection_label')}
                </Text>
                <Text className="mb-3.5 text-base" color="black" weight="normal">
                    {variantConfig.questionText}
                </Text>
                <RadioGroup name="selectedProjectVariant" className="gap-3">
                    {variantConfig.variants.map(variant => (
                        <Radio key={variant.slug} value={variant.slug}>
                            {variant.label}
                        </Radio>
                    ))}
                </RadioGroup>
            </div>
        </>
    );
};

// ============================================
// Submission Format Components
// ============================================

// Configuration for link format sections
const LINK_FORMAT_CONFIG: Record<string, { labelKey: string; descriptionKey: string }> = {
    [SUBMISSION_FORMAT_GITHUB_LINK]: {
        labelKey: 'submission_form.format_github_link_label',
        descriptionKey: 'submission_form.format_github_link_description',
    },
    [SUBMISSION_FORMAT_PRESENTATION_LINK]: {
        labelKey: 'submission_form.format_presentation_link_label',
        descriptionKey: 'submission_form.format_presentation_link_description',
    },
    [SUBMISSION_FORMAT_GENERIC_LINK]: {
        labelKey: 'submission_form.format_generic_link_label',
        descriptionKey: 'submission_form.format_generic_link_description',
    },
};

// Decides how a failed upload should be surfaced.
//
// `inlineFormatType` is set only when the server attributed the message to a format that actually
// renders a LinkFormatSection. That guard matters: the file-format sections ignore server errors, so
// routing a `pdf`-attributed message inline would show the student nothing at all — no inline
// message and no generic alert. Anything we can't place inline falls back to the generic alert.
//
// Note that error bodies are NOT camelcased on the way in: convertQueryCasing only transforms
// result.data, so `submission_format_type` stays in the snake_case the server sent it in.
//
// `renderedFormatTypes` is the project's own submission formats. Both conditions are needed: the
// format has to render a LinkFormatSection at all, *and* this project has to be showing it. The
// server never cross-checks an attachment's format type against the learner project's formats (see
// the comment on presentation_link_attachment), so a hand-crafted request can be rejected for a
// format this form never rendered — and routing that inline would put the message in a section that
// doesn't exist.
//
// Exported for testing
export const parseSubmissionError = (error: unknown, renderedFormatTypes: SubmissionFormatType[]) => {
    const errorData = error as { data?: { message?: string; submission_format_type?: SubmissionFormatType } };
    const message = errorData?.data?.message;
    const formatType = errorData?.data?.submission_format_type;
    const canRenderInline =
        !!formatType && !!LINK_FORMAT_CONFIG[formatType] && renderedFormatTypes.includes(formatType);

    return { message, inlineFormatType: canRenderInline ? formatType : undefined };
};

// Finds the rendered section for a submission format. onSubmit uses this to scroll a rejected link
// into view, and LinkFormatSection renders the matching data attribute; a spec asserts the two
// still agree, since a mismatch would silently no-op.
//
// Exported for testing
export const submissionFormatSectionSelector = (formatType: SubmissionFormatType) =>
    `[data-submission-format="${formatType}"]`;

// Scrolls a rejected link's section into view. Extracted so a spec can exercise the wiring —
// selector to element to scrollIntoView — which is the half where the original defect lived: the
// selector was fine in isolation, but it targeted a `name` attribute the input never rendered.
//
// Exported for testing
export const scrollSubmissionFormatIntoView = (formatType: SubmissionFormatType) =>
    document
        .querySelector(submissionFormatSectionSelector(formatType))
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });

// Reusable component for link format sections (GitHub, Presentation, Generic)
// Exported for testing
export const LinkFormatSection = memo(
    ({
        formatType,
        serverError,
        isSubmitting,
    }: {
        formatType: SubmissionFormatType;
        serverError?: ServerFieldError;
        isSubmitting?: boolean;
    }) => {
        const { t } = useTranslation('back_royal');
        const config = LINK_FORMAT_CONFIG[formatType];
        const { control } = useFormContext();
        const fieldName = `submissionsByFormat.${formatType}.url`;
        const urlValue = useWatch({ control, name: fieldName }) as string;

        const trimmedUrl = urlValue?.trim() ?? '';
        const hasValue = trimmedUrl.length > 0;

        // For GitHub links, show error if URL is non-empty but not a valid GitHub URL
        const isGitHubLink = formatType === SUBMISSION_FORMAT_GITHUB_LINK;
        const isInvalidGitHubUrl = isGitHubLink && hasValue && !isValidGitHubUrl(trimmedUrl);

        // For presentation links, show error if URL is non-empty but not a valid URL
        const isPresentationLink = formatType === SUBMISSION_FORMAT_PRESENTATION_LINK;
        const isInvalidPresentationUrl = isPresentationLink && hasValue && !isValidUrl(trimmedUrl);

        // A rejection the server attributed to this field (e.g. a Google Drive folder link) is
        // shown here rather than in the form's generic submit-error alert, so the student can see
        // which input to fix.
        //
        // Deliberately NOT stored as a react-hook-form error: setError drives isValid false, which
        // disables Submit until the field changes. That would strand the most common rejection —
        // `unreachable` tells the student to fix sharing in Google Drive and submit the same link
        // again, so requiring a URL edit to re-enable Submit makes the instruction impossible to
        // follow. Server feedback is transient, not validation state.
        //
        // Showing it only while the URL still matches the one that was rejected means editing the
        // field clears the message for free, with no clear-on-change wiring.
        const serverErrorMessage = serverError?.url === trimmedUrl ? serverError.message : undefined;

        const isInvalid = isInvalidGitHubUrl || isInvalidPresentationUrl || !!serverErrorMessage;
        const errorMessage = isInvalidGitHubUrl
            ? (t('submission_form.format_github_link_error') as string)
            : isInvalidPresentationUrl
              ? (t('submission_form.format_presentation_link_error') as string)
              : serverErrorMessage;

        return (
            // The data attribute is onSubmit's scroll target after a server rejection. It can't
            // query the input by name: Input passes `name` to react-hook-form's Controller, but
            // VanillaInput destructures its props explicitly and never forwards it, so the rendered
            // <input> has no name attribute. Scrolling the whole section is better anyway — it
            // brings the message into view along with the field it belongs to.
            <div className="my-0" data-submission-format={formatType}>
                <Text as="h3" color="black" className="mb-[10px]">
                    {t(config.labelKey)}
                </Text>
                <Text className="mb-3.5 text-base" color="black" weight="normal">
                    {t(config.descriptionKey)}
                </Text>
                <Input
                    name={fieldName}
                    label={t('submission_form.link_input_label') as string}
                    isInvalid={isInvalid}
                    errorMessage={errorMessage}
                    // Disabled for the whole submission, not just the Drive probe — isSubmitting is
                    // the upload mutation's lifetime, which for a request carrying a file is however
                    // long the multipart upload takes. The Submit button is already disabled for that
                    // same period, so the only cost is that a student can't stage a fix mid-upload.
                    //
                    // Needed because the section shows a server message only while the field still
                    // holds the value that was rejected: an edit mid-request would suppress it, and
                    // onSubmit's early return skips the generic alert, leaving a 422 with no feedback
                    // at all.
                    disabled={isSubmitting}
                />
            </div>
        );
    },
);

// PDF format section
const PdfFormatSection = memo(({ formatType }: { formatType: SubmissionFormatType }) => {
    const { t } = useTranslation('back_royal');

    return (
        <div className="my-0">
            <Text as="h3" color="black" className="mb-[10px]">
                {t('submission_form.format_pdf_label')}
            </Text>
            <Text className="mb-3.5 text-base" color="black" weight="normal">
                {t('submission_form.format_pdf_description')}
            </Text>
            <FileInput
                accept={MIME_TYPE_PDF}
                buttonText={t('submission_form.file_input_button_text') as string}
                name={`submissionsByFormat.${formatType}.files`}
                note={t('submission_form.file_input_note') as string}
                validators={[
                    fileTypeValidator(MIME_TYPE_PDF, t('submission_form.file_type_error_pdf') as string),
                    maxSizeMBValidator(MAX_FILE_SIZE_MB),
                ]}
            />
        </div>
    );
});

// Spreadsheet format section - has unique Google Sheets guidance text
const SpreadsheetFormatSection = memo(({ formatType }: { formatType: SubmissionFormatType }) => {
    const { t } = useTranslation('back_royal');

    return (
        <div className="my-0">
            <Text as="h3" color="black" className="mb-[10px]">
                {t('submission_form.format_spreadsheet_label')}
            </Text>
            <Text className="mb-3.5 text-base" color="black" weight="normal">
                {t('submission_form.format_spreadsheet_description')}
            </Text>
            <Text className="mb-3.5 text-sm italic" color="slate-grey" weight="normal">
                {t('submission_form.spreadsheet_google_sheets_guidance')}
            </Text>
            <FileInput
                accept={MIME_TYPE_XLSX}
                buttonText={t('submission_form.file_input_button_text') as string}
                name={`submissionsByFormat.${formatType}.files`}
                note={t('submission_form.file_input_note') as string}
                validators={[
                    fileTypeValidator(MIME_TYPE_XLSX, t('submission_form.file_type_error_xlsx') as string),
                    maxSizeMBValidator(MAX_FILE_SIZE_MB),
                ]}
            />
        </div>
    );
});

// ZIP format section
const ZipFormatSection = memo(({ formatType }: { formatType: SubmissionFormatType }) => {
    const { t } = useTranslation('back_royal');

    return (
        <div className="my-0">
            <Text as="h3" color="black" className="mb-[10px]">
                {t('submission_form.format_zip_label')}
            </Text>
            <Text className="mb-3.5 text-base" color="black" weight="normal">
                {t('submission_form.format_zip_description')}
            </Text>
            <FileInput
                accept={MIME_TYPE_ZIP}
                buttonText={t('submission_form.file_input_button_text') as string}
                name={`submissionsByFormat.${formatType}.files`}
                note={t('submission_form.file_input_note') as string}
                validators={[
                    fileTypeValidator(MIME_TYPE_ZIP, t('submission_form.file_type_error_zip') as string),
                    maxSizeMBValidator(MAX_FILE_SIZE_MB),
                ]}
            />
        </div>
    );
});

// Renders the appropriate component for a given submission format type
const SubmissionFormatSection = memo(
    ({
        formatType,
        serverError,
        isSubmitting,
    }: {
        formatType: SubmissionFormatType;
        serverError?: ServerFieldError;
        isSubmitting?: boolean;
    }) => {
        switch (formatType) {
            case SUBMISSION_FORMAT_GITHUB_LINK:
            case SUBMISSION_FORMAT_PRESENTATION_LINK:
            case SUBMISSION_FORMAT_GENERIC_LINK:
                return (
                    <LinkFormatSection formatType={formatType} serverError={serverError} isSubmitting={isSubmitting} />
                );
            case SUBMISSION_FORMAT_PDF:
                return <PdfFormatSection formatType={formatType} />;
            case SUBMISSION_FORMAT_SPREADSHEET:
                return <SpreadsheetFormatSection formatType={formatType} />;
            case SUBMISSION_FORMAT_ZIP:
                return <ZipFormatSection formatType={formatType} />;
            default:
                return null;
        }
    },
);

// Section that iterates over submissionFormats array and renders each format
// Exported for testing — a spec covers that a serverFieldErrors entry reaches the matching
// LinkFormatSection, which is the prop threading the inline-rejection path depends on.
export const SubmissionFormatsSection = memo(
    ({
        project,
        serverFieldErrors,
        isSubmitting,
    }: {
        project: LearnerProject;
        serverFieldErrors: ServerFieldErrors;
        isSubmitting?: boolean;
    }) => (
        <>
            {(project.submissionFormats ?? []).map(format => (
                <div key={format.type}>
                    <Divider />
                    <SubmissionFormatSection
                        formatType={format.type}
                        serverError={serverFieldErrors[format.type]}
                        isSubmitting={isSubmitting}
                    />
                </div>
            ))}
        </>
    ),
);

const RequirementsMarkdownAnchor = ({ children, ...rest }: AnchorHTMLAttributes<HTMLAnchorElement>) => (
    <a {...rest} className="hover:text-primary-hover text-primary underline">
        {children}
    </a>
);

const RequirementsMarkdownUl = ({ children }: HTMLAttributes<HTMLElement>) => (
    <ul className="my-5 ms-5 list-disc space-y-0">{children}</ul>
);

const RequirementsSection = ({
    project,
    projectGroupMembership,
}: {
    project: LearnerProject;
    projectGroupMembership: Nullable<ProjectGroupMembershipSansProjectGroup>;
}) => {
    const { t } = useTranslation('back_royal');
    const { submissionRequirements } = project;

    return (
        <>
            <Divider />
            <div className="my-0">
                {submissionRequirements && (
                    <>
                        <Text as="h3" color="black" className="mb-[10px]">
                            {t(
                                project.projectType === 'presentation'
                                    ? 'submission_form.requirements_label_presentation'
                                    : 'submission_form.requirements_label_standard_capstone',
                            )}
                        </Text>
                        <RenderMarkdown
                            className="text-base text-black"
                            components={{ a: RequirementsMarkdownAnchor, ul: RequirementsMarkdownUl }}
                        >
                            {submissionRequirements}
                        </RenderMarkdown>
                    </>
                )}
                <Checkbox name="confirmsProjectMeetsRequirements">
                    {t('submission_form.requirements_confirm_label')}
                </Checkbox>
                {project.groupProject && projectGroupMembership?.status === ProjectGroupMembershipStatus.ACCEPTED && (
                    <Checkbox className="mt-1" name="confirmsGroupProjectSharedScoreAndFeedback">
                        {t('submission_form.requirements_confirm_group_project_label')}
                    </Checkbox>
                )}
            </div>
        </>
    );
};

const SubmissionSection = ({ isLoading, isValid }: { isLoading: boolean; isValid: boolean }) => {
    const { t } = useTranslation('back_royal');

    return (
        <>
            <Button
                isDisabled={isLoading || !isValid}
                type="submit"
                color="black"
                height={12.5}
                className="my-7.5 pt-1 uppercase"
            >
                {t(isLoading ? 'submission_form.submitting' : 'submission_form.submit_project')}
            </Button>
            {isLoading && <FrontRoyalSpinner className="no-top-margin no-delay mb-7.5" />}
        </>
    );
};

const NoSubmissionForm = ({
    message,
    closeModal,
    children,
}: {
    message: string | React.ReactNode;
    closeModal: () => void;
    children?: React.ReactNode;
}) => {
    const { t } = useTranslation('back_royal');

    return (
        <div className="flex h-full flex-col">
            <Text className="text-base" color="slate-grey" weight="normal">
                {message}
            </Text>
            {children}
            <Button color="black" height={12.5} className="my-7.5 pt-1 uppercase" onPress={closeModal}>
                {t('submission_form.return_to_dashboard')}
            </Button>
        </div>
    );
};

const Submitted = ({
    alreadySubmitted,
    project,
    projectGroup,
    projectGroupMembership,
    closeModal,
}: {
    alreadySubmitted: boolean;
    project: LearnerProject;
    projectGroup: Nullable<ProjectGroupWithMemberships>;
    projectGroupMembership: Nullable<ProjectGroupMembershipSansProjectGroup>;
    closeModal: () => void;
}) => {
    const { t } = useTranslation('back_royal');
    const brandConfig = useTargetBrandConfig();
    const projectEmail = brandConfig.emailAddressForUsername('projects');

    let messageKey = '';

    if (alreadySubmitted) {
        messageKey = 'submission_form.project_already_submitted';
    } else if (project.groupProject && projectGroupMembership?.status === ProjectGroupMembershipStatus.ACCEPTED) {
        messageKey = 'submission_form.group_project_submitted_success';
    } else {
        messageKey = 'submission_form.individual_project_submitted_success';
    }

    const message = (
        <Trans
            i18nKey={messageKey}
            t={t}
            components={{
                'semi-strong': <span className="font-semibold" />,
                email: <MailtoLink email={projectEmail} key="projects-mailto" />,
            }}
        />
    );
    const members = projectGroup
        ? projectGroup.memberships
              .filter(membership =>
                  [ProjectGroupMembershipStatus.ACCEPTED, ProjectGroupMembershipStatus.INVITED].includes(
                      membership.status,
                  ),
              )
              .map(getMemberDetailsFromProjectGroupMembership)
        : [];
    return (
        <NoSubmissionForm message={message} closeModal={closeModal}>
            {members.length > 0 && <MembersList className={{ base: 'mb-2.5 mt-7.5' }} members={members} />}
        </NoSubmissionForm>
    );
};

// The ProjectSubmissionForm gets rendered from 3 different places, but only one of them (the schedule box) would let it open when
// the project has been waived.  Someday we may not need this.
const Waived = ({ closeModal }: { closeModal: () => void }) => {
    const { t } = useTranslation('back_royal');
    const message = t('submission_form.project_waived');

    return <NoSubmissionForm message={message} closeModal={closeModal} />;
};

// Build initial default values for submissionsByFormat based on project's submission formats
const buildSubmissionsByFormatDefaults = (
    project: LearnerProject,
): Record<SubmissionFormatType, SubmissionFormatFormValue> => {
    const defaults = {} as Record<SubmissionFormatType, SubmissionFormatFormValue>;
    (project.submissionFormats ?? []).forEach(format => {
        defaults[format.type] = { files: [], url: '' };
    });
    return defaults;
};

// FIXME: when we get rid of createOutsideProjectSubmissionModal, I'd like to rename closeModal to onClose to be more consistent.
// We use closeModal because that's how the onClose function is passed down into the children in the Modal component.
// oxlint-disable-next-line max-lines-per-function
const ProjectSubmissionFormComponent = ({
    project,
    projectGroupMembership,
    closeModal,
    onCreateGroup,
    onViewGroup,
    targetUser,
    cohortId,
    onUploadSuccess,
}: {
    project: LearnerProject;
    projectGroupMembership: Nullable<ProjectGroupMembershipSansProjectGroup>;
    closeModal: () => void;
    onCreateGroup: () => void;
    onViewGroup: () => void;
    // Admin mode props - when targetUser is provided, the form operates in admin mode
    targetUser?: AdminUploadTargetUser;
    cohortId?: string;
    // Callback when upload succeeds - called with response data before showing success state
    onUploadSuccess?: (response: UploadProjectSubmissionResponse) => void;
}) => {
    const { t } = useTranslation('back_royal');
    const currentUser = useCurrentUser();
    const [submit, { isLoading }] = uploadProjectSubmissionApi.useCreateMutation();

    // Admin mode: when uploading on behalf of another user
    const isAdminMode = !!targetUser && !!cohortId;

    // FIXME: this is only here for the schedule-box submission case. The schedule-box (Angular inside React) doesn't respect project progress and will show
    // the "submit project" button for the entire period if the period contains a project. We cannot easily get progress into the schedule box without fetching it
    // in the top of the component tree (student dashboard) and passing it down through the various configs and eventually into the schedule-box's scope.
    // We could also theoretically introduce a schedule-box directive (it's just a template right now) and fetch progress outside of the context of a React hook.
    // However, this behavior (schedule-box not respecting project progress) is long-standing and doesn't feel worth the effort given that we're actively rewriting
    // sidebar boxes in React today.
    //
    // If the user has already submitted this project, we want to render the modal in the submitted state.
    // We can get rid of this when we rewrite the schedule-box in React and use the `useContentProgress` hook there.
    //
    // In admin mode, we skip this check - admins can always upload regardless of previous submission status.
    const { projectProgressByRequirementIdentifier } = useContentProgress();
    const progress = isAdminMode ? null : projectProgressByRequirementIdentifier[project.requirementIdentifier];
    const alreadySubmitted = useRef(!!(progress && !progress.allowResubmission));
    const waived = progress?.waived;

    const [submitted, setSubmitted] = useState(alreadySubmitted.current);
    const [projectGroup, setProjectGroup] = useState<Nullable<ProjectGroupWithMemberships>>(null);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [serverFieldErrors, setServerFieldErrors] = useState<ServerFieldErrors>(NO_SERVER_FIELD_ERRORS);

    const hasVariants = !!project.variantConfig?.variants;

    const formFunctions = useForm<ProjectSubmissionFormValues>({
        context: {
            isGroupProject: project.groupProject,
            collaborationType:
                projectGroupMembership?.status === ProjectGroupMembershipStatus.ACCEPTED ? 'group' : 'individually',
            submissionFormats: project.submissionFormats ?? [],
            hasVariants,
        },
        defaultValues: {
            confirmsProjectMeetsRequirements: false,
            confirmsGroupProjectSharedScoreAndFeedback: false,
            groupProjectAgreementFiles: [],
            submissionsByFormat: buildSubmissionsByFormatDefaults(project),
            selectedProjectVariant: '',
        },
        resolver: yupResolver(projectSubmissionFormSchema),
        mode: 'all',
    });

    const {
        formState: { isValid },
        handleSubmit,
    } = formFunctions;

    const onSubmit = handleSubmit(async values => {
        setSubmitError(null);
        setServerFieldErrors(NO_SERVER_FIELD_ERRORS);
        const adminContext = isAdminMode ? { targetUser, cohortId } : undefined;
        const result = await submit(formatFormValues(currentUser, project, values, adminContext));
        if (result.error) {
            const { message, inlineFormatType } = parseSubmissionError(
                result.error,
                (project.submissionFormats ?? []).map(format => format.type),
            );
            const submissionsByFormat = values.submissionsByFormat as Record<
                SubmissionFormatType,
                SubmissionFormatFormValue
            >;

            // When the server attributes the message to a link input, show it beneath that input
            // instead of in the generic alert at the bottom of the form, and bring the field into
            // view — on a short viewport the inline message can otherwise be off-screen, making
            // Submit look like it did nothing.
            //
            // `values` is a snapshot, not a view of live form state: with a yupResolver and Controller
            // wiring, neither submissionsByFormat nor its nested per-format objects are shared with
            // react-hook-form's internals, so reading a url out of it here still yields what was
            // submitted even if the field changed meanwhile. Established by probe — an earlier comment
            // here claimed the opposite, on the strength of a probe whose edit landed before the
            // resolver ran, which made a snapshot look like an alias.
            //
            // Belt and braces would be to compare this url against the live one and fall back to the
            // generic alert when they differ. That would work, and is deliberately left out: the link
            // inputs are disabled for the duration of the submission, so the edit it guards against
            // can't happen, and one mechanism is better than two.
            if (inlineFormatType && message) {
                setServerFieldErrors({
                    [inlineFormatType]: {
                        message,
                        url: submissionsByFormat[inlineFormatType]?.url?.trim() ?? '',
                    },
                });
                scrollSubmissionFormatIntoView(inlineFormatType);
                return;
            }

            setSubmitError((message || t('submission_form.submission_error_generic')) as string);
            return;
        }
        if (result.data.meta.projectGroup) {
            setProjectGroup(result.data.meta.projectGroup);
        }
        // Call onUploadSuccess callback with response data
        // This allows parent components (like admin upload modal) to handle the response
        onUploadSuccess?.(result.data);

        // In admin mode, close the modal immediately instead of showing the success state
        // The "Return to Dashboard" message doesn't make sense for admin uploads
        if (isAdminMode) {
            closeModal();
        } else {
            setSubmitted(true);
        }
    });

    return (
        <div className="mx-7.5">
            {(waived && <Waived closeModal={closeModal} />) ||
                (submitted && (
                    <>
                        <SubHeader className="mb-7.5" project={project} />
                        <Submitted
                            alreadySubmitted={alreadySubmitted.current}
                            project={project}
                            projectGroup={projectGroup}
                            projectGroupMembership={projectGroupMembership}
                            closeModal={closeModal}
                        />
                    </>
                )) || (
                    <FormProvider {...formFunctions}>
                        <form className="flex h-full flex-col" onSubmit={onSubmit}>
                            <SubHeader className="mb-7.5" project={project} />
                            <Description
                                project={project}
                                projectGroupMembership={projectGroupMembership}
                                onCreateGroup={onCreateGroup}
                                onViewGroup={onViewGroup}
                                isAdminMode={isAdminMode}
                            />
                            <GroupProjectSection project={project} projectGroupMembership={projectGroupMembership} />
                            <VariantSelectionSection project={project} />
                            <SubmissionFormatsSection
                                project={project}
                                serverFieldErrors={serverFieldErrors}
                                isSubmitting={isLoading}
                            />
                            <RequirementsSection project={project} projectGroupMembership={projectGroupMembership} />
                            {submitError && (
                                <div className="mb-4 rounded border border-red-400 bg-red-100 p-3 text-red-700">
                                    {submitError}
                                </div>
                            )}
                            <SubmissionSection isLoading={isLoading} isValid={isValid} />
                        </form>
                    </FormProvider>
                )}
        </div>
    );
};

export const ProjectSubmissionForm = memo(ProjectSubmissionFormComponent);
